<template>
  <div class="clients-shell">
    <base-dialog :show="!!error" title="An error occurred!" @close="handleError">
      <p>{{ error }}</p>
    </base-dialog>
    <transition name="toast">
      <div
        v-if="baseToast.message"
        class="clients-toast"
        :class="'is-' + (baseToast.type || 'info')"
      >
        <span class="dot"></span>
        <span>{{ baseToast.message }}</span>
      </div>
    </transition>

    <transition name="fade" mode="out-in">
      <!-- Form view (create/update) — no wrapper card. Modern inline
           breadcrumb-style header: back link + status chip + name. -->
      <section key="form" v-if="isCreateClient" class="form-view">
        <div class="form-head">
          <button
            type="button"
            class="back-link"
            :disabled="isLoading"
            @click="backButtonClicked"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4">
              <path d="M15 18l-6-6 6-6" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
            <span>Clients</span>
          </button>
          <span class="head-sep">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6">
              <path d="M9 6l6 6-6 6" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </span>
          <span
            class="head-chip"
            :class="mode === 'create' ? 'head-chip-new' : 'head-chip-edit'"
          >{{ mode === 'create' ? 'New' : 'Editing' }}</span>
          <span v-if="mode === 'update'" class="head-name">
            {{ selectedClient.clientName }}
            <span v-if="selectedClient.username" class="head-name-sub">@{{ selectedClient.username }}</span>
          </span>
          <span v-else class="head-name">Client</span>
        </div>

        <div v-if="isLoading" class="loading-block">
          <div class="loading-spinner"></div>
          <div class="loading-label">{{ getFormTextLoading }}</div>
        </div>
        <client-form
          v-else
          :mode="mode"
          :selected-client="selectedClient"
          @create-client="createClient"
        ></client-form>
      </section>

      <!-- List view -->
      <section key="list" v-else class="list-view">
        <header class="clients-header">
          <div>
            <div class="eyebrow">Vendor</div>
            <h1>Clients</h1>
            <p class="subtitle">Manage client accounts, credentials, commission rates, and status.</p>
          </div>
          <div class="header-actions">
            <button class="btn btn-primary" @click="createBtnClicked">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" class="btn-icon">
                <path d="M12 5v14M5 12h14" stroke-linecap="round" />
              </svg>
              New Client
            </button>
          </div>
        </header>

        <clients-list
          @edit-client="editClient"
          @create-clicked="createBtnClicked"
        ></clients-list>
      </section>
    </transition>
  </div>
</template>

<script>
import ClientsList from "./clients_list.vue";
import ClientForm from "../../components/clients/ClientForm.vue";

export default {
  name: "clients",
  components: {
    ClientsList,
    ClientForm,
  },
  data() {
    return {
      isLoading: false,
      error: null,
      isCreateClient: false,
      baseToast: { type: "", message: "" },
      mode: "create",
      selectedClient: Object,
    };
  },
  computed: {
    getFormTextLoading() {
      return this.mode === "create" ? "Creating…" : "Updating…";
    },
  },
  methods: {
    backButtonClicked() {
      this.mode = "create";
      this.isCreateClient = false;
      this.selectedClient = Object;
    },
    createBtnClicked() {
      this.mode = "create";
      this.isCreateClient = true;
      this.selectedClient = Object;
    },
    handleError() {
      this.error = null;
    },
    handleBaseToast() {
      this.baseToast = { type: "", message: "" };
    },
    async createClient(data) {
      this.isLoading = true;
      try {
        if (this.mode === "create") {
          await this.$store.dispatch("clients/createClient", data);
          this.baseToast.message = "Client created successfully!";
        } else {
          await this.$store.dispatch("clients/updateClient", data);
          this.baseToast.message = "Client updated successfully!";
        }
        this.isCreateClient = false;
        this.baseToast.type = "success";
        setTimeout(() => this.handleBaseToast(), 1200);
      } catch (error) {
        this.error = error.message || "Something failed!";
      }
      this.isLoading = false;
    },
    editClient(client) {
      this.mode = "update";
      this.isCreateClient = true;
      this.selectedClient = client;
    },
  },
};
</script>

<style scoped>
/* ------------- Shell ------------- */
.clients-shell {
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 24px;
  gap: 20px;
  background: linear-gradient(180deg, #fbfaf7 0%, #f4f0e5 100%);
  overflow-y: auto;
}
.list-view, .form-view {
  display: flex;
  flex-direction: column;
  gap: 20px;
  flex: 1;
  min-height: 0;
}

/* ------------- Header ------------- */
.clients-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}
.eyebrow {
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #1B2A47;
  font-weight: 600;
  margin-bottom: 4px;
}
.clients-header h1 {
  font-size: 28px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.1;
  margin: 0;
}
.subtitle {
  font-size: 13px;
  color: #64748b;
  margin: 4px 0 0;
}

/* ------------- Buttons ------------- */
.btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.15s ease;
  outline: none;
}
.btn:disabled { opacity: 0.5; cursor: not-allowed; }
.btn-primary {
  background: linear-gradient(135deg, #1B2A47 0%, #2C3E63 100%);
  color: #fff;
  box-shadow: 0 6px 16px -6px rgba(27, 42, 71, 0.5);
}
.btn-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 10px 24px -8px rgba(27, 42, 71, 0.55);
}
.btn-ghost {
  background: #fff;
  color: #475569;
  border-color: #e2e8f0;
}
.btn-ghost:hover:not(:disabled) { background: #f8fafc; border-color: #cbd5e1; }
.btn-icon { width: 15px; height: 15px; }

/* ------------- Form head (breadcrumb-style inline title) ------------- */
.form-head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 2px 4px;
  flex-wrap: wrap;
  min-height: 32px;
}
.back-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px 6px 6px;
  border: none;
  background: transparent;
  color: #475569;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border-radius: 8px;
  transition: color 0.12s, background 0.12s;
}
.back-link:disabled { opacity: 0.5; cursor: not-allowed; }
.back-link:hover:not(:disabled) {
  color: #1B2A47;
  background: #eef2f7;
}
.back-link svg { width: 16px; height: 16px; }

.head-sep {
  color: #cbd5e1;
  display: inline-flex;
  align-items: center;
}
.head-sep svg { width: 14px; height: 14px; }

.head-chip {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}
.head-chip-new {
  background: linear-gradient(135deg, #1B2A47 0%, #2C3E63 100%);
  color: #fff;
  box-shadow: 0 4px 10px -4px rgba(27, 42, 71, 0.45);
}
.head-chip-edit {
  background: #ecfdf5;
  color: #047857;
  border: 1px solid #a7f3d0;
}

.head-name {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.01em;
  display: inline-flex;
  align-items: baseline;
  gap: 8px;
  min-width: 0;
}
.head-name-sub {
  font-size: 12px;
  font-weight: 500;
  color: #94a3b8;
  letter-spacing: 0;
  text-transform: none;
}

.loading-block {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 60px 20px;
}
.loading-spinner {
  width: 44px; height: 44px;
  border-radius: 999px;
  border: 3px solid #e2e8f0;
  border-top-color: #1B2A47;
  animation: spin 0.9s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
.loading-label {
  font-size: 14px;
  font-weight: 600;
  color: #475569;
}

/* ------------- Toast ------------- */
.clients-toast {
  position: fixed;
  top: 24px; right: 24px;
  z-index: 60;
  background: #0f172a; color: #fff;
  padding: 12px 18px;
  border-radius: 12px;
  font-size: 13px; font-weight: 500;
  display: inline-flex; align-items: center; gap: 10px;
  box-shadow: 0 12px 30px -10px rgba(15, 23, 42, 0.4);
}
.clients-toast .dot {
  width: 8px; height: 8px; border-radius: 999px; background: #94a3b8;
}
.clients-toast.is-success { background: #059669; }
.clients-toast.is-success .dot { background: #a7f3d0; }
.clients-toast.is-error { background: #dc2626; }
.clients-toast.is-error .dot { background: #fecaca; }

/* ------------- Transitions ------------- */
.toast-enter-active, .toast-leave-active { transition: all 0.24s cubic-bezier(0.4, 0, 0.2, 1); }
.toast-enter-from, .toast-leave-to { transform: translateY(-12px); opacity: 0; }
.fade-enter-active, .fade-leave-active { transition: opacity 0.24s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
</style>
